import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import {
  getLocalizedName,
  getOrderItemTotal,
} from '../../../../utility/util/util.service';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { connect } from 'react-redux';
import { addCartItem } from '../../../../redux/store/store.actions';
import { setIsSelectAdditionsModalOpen } from '../../../../redux/modal/modal.actions';
import QtySelector from '../../qty-selector/qty-selector.component';
import AdditionGroup from './addition-group.component';
import { ORDER_TYPE_PICKUP } from '../../../../utility/constants';

const SelectAdditionsModal = ({
  isOpen,
  data,
  cart,
  checkout,

  setIsSelectAdditionsModalOpen,
  addCartItem,
  dailyLimits,
}) => {
  const intl = useIntl();
  const [itemQty, setItemQty] = useState(1); // qty of the whole product (not addition!)
  const [satisfiedGroups, setSatisfiedGroups] = useState([]);
  const [selectedOrderItemAdditions, setSelectedOrderItemAdditions] = useState(
    [],
  ); // array of {qty: number, additionGroupEntry: ItemAdditionGroupEntry}
  const [isEditMode, setIsEditMode] = useState(false);
  const { itemData, itemAdditionGroups } = data || {};

  // Stock limit: compute max orderable qty for this item
  const limitInfo = dailyLimits?.find((dl) => dl.itemDataId === itemData?.id);
  const remaining = limitInfo
    ? Math.max(0, limitInfo.limit - limitInfo.orderedCount)
    : null;
  // In edit mode, the item is already in cart — so its current qty doesn't count against the limit
  const otherCartQty = cart
    ?.filter((ci) => ci.itemData?.id === itemData?.id)
    ?.reduce((s, ci) => s + (ci.qty || 0), 0) || 0;
  const editingQty = isEditMode ? (data?.editOrderItem?.qty || 0) : 0;
  const maxQty = remaining != null ? Math.max(1, remaining - otherCartQty + editingQty) : 999;
  // Use orderType for price calculation, fallback to PICKUP for takeaway prices when no orderType is set
  const priceOrderType = checkout?.orderType || ORDER_TYPE_PICKUP;
  const total = getOrderItemTotal(priceOrderType, {
    qty: itemQty,
    itemData,
    additions: selectedOrderItemAdditions,
  });
  const totalFormatted = intl.formatNumber(total, { format: 'priceCurrency' });
  const itemName = getLocalizedName(itemData, intl.locale);

  // Reset state when modal opens with new data
  useEffect(() => {
    if (isOpen && data) {
      setSatisfiedGroups([]);
      setItemQty(1);
      setIsEditMode(false);

      if (data?.editOrderItem) {
        // Edit mode: load existing additions
        setSelectedOrderItemAdditions(data?.editOrderItem?.additions || []);
        setItemQty(data?.editOrderItem?.qty || 1);
        setIsEditMode(true);
      } else {
        // New item: start fresh
        setSelectedOrderItemAdditions([]);
      }
    }
  }, [isOpen, data]);

  const closeModal = (additionsSelected) => {
    //additionsSelected is bool value, if user clicks on button save additions we want to save new additions combination,
    //if the user just closes the modal we want to save prev state
    if (isEditMode && !additionsSelected) {
      addCartItem(
        cart,
        itemData,
        data?.editOrderItem?.additions,
        data?.editOrderItem?.qty,
        priceOrderType,
      );
    }
    setSatisfiedGroups([]);
    setIsSelectAdditionsModalOpen(false);
    setSelectedOrderItemAdditions([]);
    setItemQty(1);
    setIsEditMode(false);
  };

  return (
    <ModalSheet
      isOpen={isOpen}
      closeModal={() => closeModal(false)}
      title={intl.formatMessage({ id: 'selectAdditionsText' }, { itemName })}
      footer={
        <div className="d-flex align-content-center">
          <div className="d-flex align-content-center flex-grow-0">
            <QtySelector
              className="ml-50 mr-2"
              value={itemQty}
              disablePlus={itemQty >= maxQty}
              onClickPlus={() => setItemQty(Math.min(itemQty + 1, maxQty))}
              onClickMinus={() => setItemQty(Math.max(1, itemQty - 1))}
            />
          </div>
          <div className="flex-grow-1">
            <Button
              data-testid="buttonAddAddition"
              color="primary"
              className="w-100"
              disabled={satisfiedGroups?.length < itemAdditionGroups?.length}
              onClick={() => {
                addCartItem(
                  cart,
                  itemData,
                  selectedOrderItemAdditions,
                  itemQty,
                  priceOrderType,
                );
                closeModal(true);
              }}
            >
              <FormattedMessage
                id="addToCartWithPriceLabel"
                values={{ price: totalFormatted }}
              />
            </Button>
          </div>
        </div>
      }
    >
      <div className="px-1">
        {itemAdditionGroups?.map((additionGroup, index) => (
          <>
            <AdditionGroup
              key={index}
              index={index}
              setSelectedOrderItemAdditions={setSelectedOrderItemAdditions}
              selectedOrderItemAdditions={selectedOrderItemAdditions}
              data={data}
              additionGroup={additionGroup}
              setIsGroupSatisfied={(isSatisfied) =>
                // an AdditionGroup marks itself as satisfied. We will collect all satisfied groups here
                setSatisfiedGroups((prevState) =>
                  [
                    ...prevState.filter(({ id }) => id !== additionGroup.id),
                    isSatisfied && additionGroup,
                  ].filter(Boolean),
                )
              }
            />
            {/*{index - 1 <
                additionGroup?.entries?.filter(
                  (entry) =>
                    additionGroup?.orderConditions?.length === 0 ||
                    getSatisfiedOrderCondition(
                      entry.orderConditions,
                      table ? 'inhouse' : 'takeaway',
                    ),
                )?.length && <hr className="mb-1 mt-2" />}*/}
          </>
        ))}
      </div>
    </ModalSheet>
  );
};

const mapStateToProps = (state) => ({
  isOpen: state.modals.isSelectAdditionsModalOpen,
  data: state.modals.selectAdditionsModalData,
  cart: state.store.cart,
  store: state.store.store,
  checkout: state.store.checkout,
  dailyLimits: state.store.dailyLimits,
});

export default connect(mapStateToProps, {
  setIsSelectAdditionsModalOpen,
  addCartItem,
})(SelectAdditionsModal);
